import { castToArray } from "../functions/cast-to-array";
import { partition } from "../functions/partition";

export type OperatorType =
	| "IN"
	| "NOT_IN"
	| "INCLUDES"
	| "MATCHES"
	| "STARTS_WITH"
	| "NOT_STARTS_WITH"
	| "ENDS_WITH"
	| "NOT_ENDS_WITH"
	| "CONTAINS"
	| "NOT_CONTAINS"
	| "GT"
	| "GTE"
	| "LT"
	| "LTE"
	| "EQUAL"
	| "NOT"
	| "SOME"
	| "NONE"
	| "IS_NULL";

const KNOWN_OPERATORS: OperatorType[] = [
	"NOT_STARTS_WITH",
	"NOT_ENDS_WITH",
	"NOT_CONTAINS",
	"STARTS_WITH",
	"ENDS_WITH",
	"INCLUDES",
	"MATCHES",
	"NOT_IN",
	"IS_NULL",
	"CONTAINS",
	"GTE",
	"LTE",
	"SOME",
	"NONE",
	"EQUAL",
	"GT",
	"LT",
	"IN",
	"NOT",
];

function parseConditionKey(key: string): { field: string; operator: OperatorType | "" } {
	// Find the longest matching operator suffix
	for (const op of KNOWN_OPERATORS) {
		const suffix = `_${op}`;
		if (key.endsWith(suffix)) {
			return { field: key.slice(0, key.length - suffix.length), operator: op };
		}
	}

	// Special cases used for connection filtering handled elsewhere
	if (key === "edge" || key === "node") {
		return { field: key, operator: "" };
	}

	// Default to EQUAL when no operator suffix provided
	return { field: key, operator: "EQUAL" };
}

export const applyFilterConditionTree = (data: any, where: any): any => {
	if (!data || !where) {
		return false;
	}

	if (Array.isArray(where)) {
		return where.every((condition) => applyFilterConditionTree(data, condition));
	}

	if (Array.isArray(data)) {
		return data.some((item) => applyFilterConditionTree(item, where));
	}

	const { AND, OR, ...rest } = where;
	const isAndMatched = !AND || castToArray(AND).every((condition) => applyFilterConditionTree(data, condition));
	const isOrMatched = !OR || !castToArray(OR).length || castToArray(OR).some((condition) => applyFilterConditionTree(data, condition));

	if (!isAndMatched || !isOrMatched) {
		return false;
	}

	const conditions = Object.entries(rest)
		.map((entry) => {
			const { field, operator } = parseConditionKey(entry[0]);
			return {
				field,
				operator: operator as OperatorType | "",
				value: entry[1] as any,
			};
		})
		.filter(({ field }) => field !== "tenantId" && field !== "tenantIds");
	const [nodeConditions, otherConditions] = partition(conditions, (condition) => condition.field === "node");
	const processedConnectionAndOtherConditions = otherConditions.map((condition) =>
		condition.field === "edge"
			? {
					field: "edges",
					operator: "" as OperatorType | "",
					value: { ...condition.value, ...(nodeConditions.length ? { node: nodeConditions[0].value } : {}) },
			  }
			: condition
	);

	return processedConnectionAndOtherConditions.every((condition) =>
		typeof condition.value === "object" &&
		!(Array.isArray(condition.value) && typeof condition.value[0] !== "object") &&
		!(condition.value instanceof Date)
			? applyFilterConditionTree(data[condition.field], condition.value)
			: isValueMatchedByCondition(data[condition.field], condition)
	);
};

const isValueMatchedByCondition = (value: any, condition: { operator: OperatorType | ""; value: string | Date }) => {
	switch (condition.operator) {
		case "IN":
			return Array.isArray(condition.value) && condition.value.includes(value);
		case "NOT_IN":
			return Array.isArray(condition.value) && !condition.value.includes(value);
		case "INCLUDES":
			return Array.isArray(value) && value.includes(condition.value);
		case "MATCHES":
			return typeof condition.value === "string" && new RegExp(condition.value).test(value);
		case "ENDS_WITH":
			return (
				typeof value === "string" &&
				typeof condition.value === "string" &&
				value.toLowerCase().startsWith(condition.value.toLowerCase())
			);
		case "NOT_ENDS_WITH":
			return (
				typeof value === "string" &&
				typeof condition.value === "string" &&
				!value.toLowerCase().startsWith(condition.value.toLowerCase())
			);
		case "CONTAINS":
			return (
				typeof value === "string" &&
				typeof condition.value === "string" &&
				value.toLowerCase().includes(condition.value.toLowerCase())
			);
		case "NOT_CONTAINS":
			return (
				typeof value === "string" &&
				typeof condition.value === "string" &&
				!value.toLowerCase().includes(condition.value.toLowerCase())
			);
		case "GT":
			return value > condition.value;
		case "GTE":
			return typeof condition.value === "object" ? value.getTime() >= condition.value.getTime() : value >= condition.value;
		case "LT":
			return value < condition.value;
		case "LTE":
			return typeof condition.value === "object" ? value.getTime() <= condition.value.getTime() : value <= condition.value;
		case "EQUAL":
		case "":
			return /\d{4}-\d{2}-\d{2}/.test(value) ? value?.startsWith(condition.value) : value === condition.value;
		default:
			throw new Error(`Unknown operator: ${condition.operator}`);
	}
};
